import { useEffect, useRef, useState } from "react";
import { Headset, X, PaperPlaneTilt, Sparkle } from "@phosphor-icons/react";
import { API, getToken } from "../lib/api";

const sid = () => {
  let s = sessionStorage.getItem("greetly_support_sid");
  if (!s) { s = crypto.randomUUID(); sessionStorage.setItem("greetly_support_sid", s); }
  return s;
};

const SUGGESTED = [
  "How do I wire up my Twilio number?",
  "What's the difference between callback and external to-go?",
  "How do I turn on Realtime voice?",
  "How do add-ons and the trial work?",
];

export default function SupportChatWidget() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([
    { role: "assistant", content: "Hi! I'm the BuzzerAI support agent (Mimi's teammate — I help operators, she handles your guests). Ask me anything about setup, add-ons, Twilio wire-up, to-go modes, or the AI voice receptionist." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [msgs, open]);

  const send = async (override) => {
    const text = (override ?? input).trim();
    if (!text || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/support/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ session_id: sid(), message: text }),
      });
      if (!res.ok) throw new Error("Support chat failed");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let acc = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        acc += dec.decode(value, { stream: true });
        setMsgs((m) => { const copy = [...m]; copy[copy.length - 1] = { role: "assistant", content: acc }; return copy; });
      }
    } catch {
      setMsgs((m) => { const copy = [...m]; copy[copy.length - 1] = { role: "assistant", content: "I'm having trouble. Try again, or drop a note in the Feedback page and we'll reply personally." }; return copy; });
    }
    setBusy(false);
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 h-12 pl-4 pr-5 rounded-full bg-[#D96C5B] text-white shadow-lg flex items-center gap-2 hover:-translate-y-0.5 transition-transform"
          data-testid="support-open"
          aria-label="Open BuzzerAI support"
        >
          <Headset size={20} weight="fill" />
          <span className="text-sm font-medium">Support</span>
        </button>
      )}
      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-2rem)] paper-card flex flex-col overflow-hidden" style={{ height: 560 }}>
          <div className="bg-[#D96C5B] text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Headset size={18} weight="fill" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/70">BuzzerAI Support</div>
                <div className="font-heading font-extrabold">Setup &amp; Help</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="support-close" className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center"><X size={16} color="#fff" /></button>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#F7F6F2]">
            {msgs.map((m, i) => (
              <div key={`support-${m.ts || i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-relaxed whitespace-pre-line ${m.role === "user" ? "bg-[#1E3A2F] text-white" : "bg-white border border-[#E1DFD5]"}`}>
                  {m.content || <span className="text-[#5C6B61]">…</span>}
                </div>
              </div>
            ))}
            {msgs.length <= 1 && (
              <div className="pt-2 space-y-2">
                <div className="text-xs text-[#5C6B61] flex items-center gap-1"><Sparkle size={12} color="#D96C5B" weight="fill" /> Common questions</div>
                {SUGGESTED.map((s) => (
                  <button key={s} onClick={() => send(s)} className="w-full text-left text-sm bg-white border border-[#E1DFD5] rounded-lg px-3 py-2 hover:border-[#1E3A2F] hover:bg-[#F7F6F2] transition-colors" data-testid={`support-suggest-${s.slice(0, 12).replace(/\W/g, '')}`}>
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="p-3 border-t border-[#E1DFD5] flex items-center gap-2">
            <input
              className="input-field !py-2 text-sm" placeholder="Ask about setup, add-ons, Twilio…"
              value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }}
              data-testid="support-input"
            />
            <button onClick={() => send()} disabled={busy} className="w-10 h-10 rounded-full bg-[#D96C5B] flex items-center justify-center disabled:opacity-50" data-testid="support-send">
              <PaperPlaneTilt size={16} color="#fff" weight="fill" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
